import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Alert,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { paymentsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const CCAvPaymentDialog = ({ 
  open, 
  onClose, 
  paymentData, 
  onPaymentSuccess,
  onPaymentError 
}) => {
  const { user, getAuthToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPaymentForm, setShowPaymentForm] = useState(true);
  const [paymentUrl, setPaymentUrl] = useState('');
  const formRef = useRef(null);

  // Form state for billing information
  const [billingForm, setBillingForm] = useState({
    billingName: user?.attributes?.given_name + ' ' + user?.attributes?.family_name || '',
    billingEmail: user?.attributes?.email || '',
    billingPhone: user?.attributes?.phone_number || '',
    billingAddress: '',
    billingCity: '',
    billingState: '',
    billingZip: '',
    paymentMethod: 'CCAvenue'
  });

  const handleInputChange = (field, value) => {
    setBillingForm(prev => ({ ...prev, [field]: value }));
  };

  const handlePayment = async () => {
    setError('');
    setLoading(true);

    try {
      // Store authentication token for callback usage
      if (getAuthToken) {
        try {
          const token = await getAuthToken();
          sessionStorage.setItem('ccavenue_auth_token', token);
        } catch (tokenError) {
          console.warn('Could not get auth token:', tokenError);
        }
      }
      // Validate required fields
      if (!billingForm.billingName || !billingForm.billingEmail || !billingForm.billingPhone) {
        setError('Please fill in all required billing information');
        setLoading(false);
        return;
      }

      if (!paymentData.amount || !paymentData.flatId || !paymentData.buildingId) {
        setError('Missing payment information');
        setLoading(false);
        return;
      }

      // Generate unique payment ID
      const paymentId = `PAY_${Date.now()}_${paymentData.flatId}`;

      // Prepare payment request
      const paymentRequest = {
        paymentId,
        amount: parseFloat(paymentData.amount),
        flatId: paymentData.flatId,
        buildingId: paymentData.buildingId,
        userId: user?.username || user?.attributes?.sub,
        paymentType: paymentData.paymentType || 'MaintenanceFee',
        description: paymentData.description || 'Apartment Maintenance Payment',
        billingName: billingForm.billingName,
        billingEmail: billingForm.billingEmail,
        billingPhone: billingForm.billingPhone,
        billingAddress: billingForm.billingAddress || 'Address'
      };

      // Initiate CCAvenue payment
      const response = await paymentsAPI.initiateCCAvPayment(paymentRequest);
      
      if (response.data && response.data.encRequest && response.data.accessCode) {
        // Create and submit the CCAvenue form in a popup window
        const { encRequest, accessCode, ccavenueUrl } = response.data;
        
        // Open a popup window for CCAvenue payment
        const paymentWindow = window.open(
          '', 
          'ccavenuePayment',
          'width=800,height=600,scrollbars=yes,resizable=yes,status=yes,location=yes'
        );
        
        if (paymentWindow) {
          // Create the payment window HTML with loading screen and direct redirect
          const paymentWindowHtml = `
            <!DOCTYPE html>
            <html>
            <head>
              <title>CCAvenue Payment</title>
              <meta charset="UTF-8">
              <meta name="viewport" content="width=device-width, initial-scale=1.0">
              <style>
                body { 
                  margin: 0;
                  padding: 0;
                  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                  height: 100vh;
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  justify-content: center;
                }
                .container {
                  background: white;
                  border-radius: 12px;
                  box-shadow: 0 20px 40px rgba(0,0,0,0.1);
                  padding: 40px;
                  text-align: center;
                  max-width: 400px;
                  width: 90%;
                }
                .logo-section {
                  margin-bottom: 30px;
                }
                .logo-section img {
                  height: 40px;
                }
                .title {
                  font-size: 24px;
                  font-weight: 600;
                  color: #333;
                  margin: 20px 0;
                }
                .subtitle {
                  font-size: 16px;
                  color: #666;
                  margin-bottom: 30px;
                }
                .spinner {
                  width: 50px;
                  height: 50px;
                  border: 4px solid #f3f3f3;
                  border-top: 4px solid #667eea;
                  border-radius: 50%;
                  animation: spin 1s linear infinite;
                  margin: 0 auto 20px;
                }
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
                .note {
                  background: #f8f9fa;
                  border-radius: 8px;
                  padding: 15px;
                  margin-top: 20px;
                  font-size: 14px;
                  color: #666;
                }
                .close-btn {
                  background: #6c757d;
                  color: white;
                  border: none;
                  padding: 10px 20px;
                  border-radius: 6px;
                  cursor: pointer;
                  font-size: 14px;
                  margin-top: 20px;
                }
                .close-btn:hover {
                  background: #5a6268;
                }
                .hidden-form {
                  display: none;
                }
              </style>
            </head>
            <body>
              <div class="container">
                <div class="logo-section">
                  <img src="https://www.ccavenue.com/images/ccavenue_logo.gif" alt="CCAvenue">
                </div>
                
                <div class="title">Secure Payment</div>
                <div class="subtitle">Redirecting to CCAvenue Payment Gateway...</div>
                
                <div class="spinner"></div>
                
                <div class="note">
                  🔒 Your payment will be processed securely by CCAvenue. 
                  <br>Please do not close this window during payment.
                </div>
                
                <button class="close-btn" onclick="closePayment()" id="closeBtn" style="display: none;">
                  Close Window
                </button>
                
                <form method="POST" action="${ccavenueUrl}" class="hidden-form" id="ccavenueForm">
                  <input type="hidden" name="encRequest" value="${encRequest}">
                  <input type="hidden" name="access_code" value="${accessCode}">
                </form>
              </div>

              <script>
                let paymentCompleted = false;
                let redirected = false;
                
                function closePayment() {
                  if (paymentCompleted || confirm('Are you sure you want to close the payment window? Your payment may not be completed.')) {
                    window.close();
                  }
                }
                
                function initializePayment() {
                  // Show the close button after a delay
                  setTimeout(() => {
                    document.getElementById('closeBtn').style.display = 'inline-block';
                  }, 5000);
                  
                  // Auto-submit the form to redirect to CCAvenue
                  setTimeout(() => {
                    if (!redirected) {
                      document.getElementById('ccavenueForm').submit();
                      redirected = true;
                    }
                  }, 2000);
                }
                
                // Monitor URL changes for callback detection
                let lastUrl = location.href;
                const checkUrlChange = () => {
                  const currentUrl = location.href;
                  if (currentUrl !== lastUrl) {
                    lastUrl = currentUrl;
                    
                    // Check if we've reached the callback URL
                    if (currentUrl.includes('payment-callback') || currentUrl.includes('myhomeentry.com')) {
                      paymentCompleted = true;
                      setTimeout(() => {
                        console.log('Payment processing completed! This window will now close.');
                        window.close();
                      }, 2000);
                    }
                  }
                };
                
                // Check for URL changes periodically
                setInterval(checkUrlChange, 1000);
                
                // Prevent accidental closure during payment
                window.addEventListener('beforeunload', function(e) {
                  if (!paymentCompleted && redirected) {
                    e.preventDefault();
                    e.returnValue = 'Payment is in progress. Are you sure you want to leave?';
                    return e.returnValue;
                  }
                });
                
                // Initialize payment when page loads
                window.onload = initializePayment;
              </script>
            </body>
            </html>
          `;
          
          paymentWindow.document.write(paymentWindowHtml);
          paymentWindow.document.close();
          
          // Hide the payment form and show waiting screen
          setShowPaymentForm(false);
          setPaymentUrl('waiting');
          
          // Add message listener for payment completion from popup
          const handlePaymentMessage = (event) => {
            console.log('Received message from popup:', event);
            
            // Accept messages from our callback domain or localhost for testing
            const allowedOrigins = [
              'https://myhomeentry.com',
              'http://localhost:3000',
              'http://127.0.0.1:3000',
              'https://localhost:3000'
            ];
            
            const isValidOrigin = allowedOrigins.some(origin => 
              event.origin === origin || event.origin.includes('localhost')
            );
            
            if (!isValidOrigin) {
              console.log('Rejected message from invalid origin:', event.origin);
              return;
            }
            
            if (event.data && event.data.type === 'PAYMENT_COMPLETED') {
              console.log('Payment completed successfully:', event.data);
              
              // Close popup if still open
              if (!paymentWindow.closed) {
                paymentWindow.close();
              }
              
              // Clean up intervals and listeners
              if (window.ccavenueCheckInterval) {
                clearInterval(window.ccavenueCheckInterval);
              }
              window.removeEventListener('message', handlePaymentMessage);
              
              // Call the success callback to show success overlay in parent
              if (onPaymentSuccess) {
                onPaymentSuccess({
                  paymentId: event.data.paymentId || paymentId,
                  status: 'COMPLETED',
                  message: 'Payment completed successfully!'
                });
              }
              
              // Close dialog
              onClose();
              
              // The parent window (OwnerTenantDashboard) will handle the page refresh
              // via the success overlay we implemented there
            } else if (event.data && event.data.type === 'PAYMENT_FAILED') {
              console.log('Payment failed:', event.data);
              
              // Close popup if still open
              if (!paymentWindow.closed) {
                paymentWindow.close();
              }
              
              // Show error message
              const errorMsg = event.data.message || 'Payment failed. Please try again.';
              setError(errorMsg);
              if (onPaymentError) {
                onPaymentError(errorMsg);
              }
              
              // Reset dialog state
              setShowPaymentForm(true);
              setPaymentUrl('');
            }
          };
          
          // Listen for messages from the popup
          window.addEventListener('message', handlePaymentMessage);
          
          // Monitor the popup window for closure
          const checkClosed = setInterval(() => {
            if (paymentWindow.closed) {
              clearInterval(checkClosed);
              window.removeEventListener('message', handlePaymentMessage);
              // Reset dialog state when popup is closed manually
              setShowPaymentForm(true);
              setPaymentUrl('');
              console.log('Payment window was closed by user');
            }
          }, 1000);
          
          // Store the interval ID for cleanup
          window.ccavenueCheckInterval = checkClosed;
          
          // Notify parent component that payment is initiated
          if (onPaymentSuccess) {
            onPaymentSuccess({
              paymentId,
              message: 'Payment window opened. Complete the payment in the popup window.'
            });
          }
        } else {
          setError('Unable to open payment window. Please check if popups are blocked.');
        }
      } else {
        setError('Failed to initiate payment. Please try again.');
      }
    } catch (err) {
      console.error('CCAvenue payment error:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Payment initiation failed';
      setError(errorMessage);
      if (onPaymentError) {
        onPaymentError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      // Cleanup any monitoring intervals
      if (window.ccavenueCheckInterval) {
        clearInterval(window.ccavenueCheckInterval);
        window.ccavenueCheckInterval = null;
      }
      
      setBillingForm({
        billingName: user?.attributes?.given_name + ' ' + user?.attributes?.family_name || '',
        billingEmail: user?.attributes?.email || '',
        billingPhone: user?.attributes?.phone_number || '',
        billingAddress: '',
        billingCity: '',
        billingState: '',
        billingZip: '',
        paymentMethod: 'CCAvenue'
      });
      setError('');
      setShowPaymentForm(true);
      setPaymentUrl('');
      onClose();
    }
  };

  const handleBackToForm = () => {
    setShowPaymentForm(true);
    setPaymentUrl('');
    setError('');
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth maxHeight="90vh">
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <img 
              src="https://www.ccavenue.com/images/ccavenue_logo.gif" 
              alt="CCAvenue" 
              style={{ height: '30px' }}
            />
            <Typography variant="h6">
              {showPaymentForm ? 'CCAvenue Payment' : 'Processing Payment...'}
            </Typography>
          </Box>
          {!showPaymentForm && (
            <Button 
              onClick={handleBackToForm} 
              size="small" 
              variant="outlined"
              sx={{ ml: 2 }}
            >
              ← Back to Form
            </Button>
          )}
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ p: showPaymentForm ? 3 : 0 }}>
        {showPaymentForm ? (
          <>
            {/* Payment Summary */}
            <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="h6" gutterBottom color="primary">
                Payment Summary
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography><strong>Amount:</strong> ₹{paymentData.amount}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography><strong>Flat ID:</strong> {paymentData.flatId}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography><strong>Payment Type:</strong> {paymentData.paymentType || 'Maintenance Fee'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography><strong>Description:</strong> {paymentData.description || 'Apartment Maintenance Payment'}</Typography>
                </Grid>
              </Grid>
            </Box>

            {/* Error Display */}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

        {/* Billing Information Form */}
        <Typography variant="h6" gutterBottom>
          Billing Information
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Full Name*"
              value={billingForm.billingName}
              onChange={(e) => handleInputChange('billingName', e.target.value)}
              disabled={loading}
              required
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Email*"
              type="email"
              value={billingForm.billingEmail}
              onChange={(e) => handleInputChange('billingEmail', e.target.value)}
              disabled={loading}
              required
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Phone Number*"
              value={billingForm.billingPhone}
              onChange={(e) => handleInputChange('billingPhone', e.target.value)}
              disabled={loading}
              required
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Payment Method</InputLabel>
              <Select
                value={billingForm.paymentMethod}
                onChange={(e) => handleInputChange('paymentMethod', e.target.value)}
                disabled={loading}
              >
                <MenuItem value="CCAvenue">CCAvenue - Credit/Debit Card, Net Banking, UPI</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Address"
              value={billingForm.billingAddress}
              onChange={(e) => handleInputChange('billingAddress', e.target.value)}
              disabled={loading}
              placeholder="Enter your billing address"
            />
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="City"
              value={billingForm.billingCity}
              onChange={(e) => handleInputChange('billingCity', e.target.value)}
              disabled={loading}
            />
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="State"
              value={billingForm.billingState}
              onChange={(e) => handleInputChange('billingState', e.target.value)}
              disabled={loading}
            />
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="PIN Code"
              value={billingForm.billingZip}
              onChange={(e) => handleInputChange('billingZip', e.target.value)}
              disabled={loading}
            />
          </Grid>
        </Grid>

            {/* Security Notice */}
            <Box sx={{ mt: 3, p: 2, bgcolor: '#e8f5e8', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                🔒 <strong>Secure Payment:</strong> You will be redirected to CCAvenue's secure payment gateway. 
                Your payment information is encrypted and processed securely.
              </Typography>
            </Box>
          </>
        ) : (
          /* Payment processing screen */
          <Box sx={{ 
            height: '50vh', 
            width: '100%', 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            textAlign: 'center',
            gap: 3
          }}>
            <CircularProgress size={60} />
            <Typography variant="h6" color="primary">
              Payment Window Opened
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 400 }}>
              Complete your payment in the popup window. This dialog will remain open to show the results.
            </Typography>
            <Box sx={{ p: 2, bgcolor: '#fff3cd', borderRadius: 1, border: '1px solid #ffeaa7' }}>
              <Typography variant="body2" color="text.secondary">
                💡 <strong>Note:</strong> If the payment window didn't open, please check if popup blockers are disabled for this site.
              </Typography>
            </Box>
            {error && (
              <Alert severity="error" sx={{ mt: 2, maxWidth: 400 }}>
                {error}
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      
      {showPaymentForm && (
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={handleClose} 
            disabled={loading}
            color="secondary"
          >
            Cancel
          </Button>
          <Button
            onClick={handlePayment}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
            sx={{ minWidth: 140 }}
          >
            {loading ? 'Processing...' : 'Proceed to Pay'}
          </Button>
        </DialogActions>
      )}
      
      {/* Hidden form for CCAvenue submission */}
      <form ref={formRef} style={{ display: 'none' }} />
    </Dialog>
  );
};

export default CCAvPaymentDialog;